'use client'

import { cn } from '@brew/ui/cn'
import { ArrowUpRight, Check } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { type ReactNode, useState } from 'react'
import { TextMorph } from 'torph/react'
import type { BillingPlanActionVariant } from '@/lib/billing/ui-state'
import { pricingCtaClass } from './pricing-cta'

export interface PricingFeature {
  id: string
  name: string
  description?: string
  highlighted?: boolean
}

export type PricingButtonVariant = BillingPlanActionVariant

export type PricingCardBadge = {
  label: string
  /** `promo` = an active offer on this tier; `legacy` = a sunset tier. */
  tone: 'promo' | 'legacy'
}

export interface PricingCardProps {
  name: string
  price: string
  /** Text beside the price ('/mo', in the reader's language, by default). */
  priceSuffix?: string
  /** Struck-through list price shown beside a discounted headline. */
  compareAtPrice?: string
  /** Extra line under the price (e.g. strikethrough list → discounted /mo). */
  priceDetail?: ReactNode
  /** Quiet sentence under the price block (the onboarding cards use it). */
  caption?: string
  /** Quiet marker opposite the plan name (offer or legacy). */
  badge?: PricingCardBadge
  /**
   * A quieter alternative buy path under the main CTA. On the promo Growth
   * card this is month-to-month at list; the featured button is the prepaid
   * term. Two controls, two amounts — a buyer must never confuse them.
   */
  secondaryCta?: {
    label: string
    onSelect: () => void
    loading?: boolean
  }
  features: Array<PricingFeature>
  isPopular?: boolean
  isCurrentPlan?: boolean
  isCustomPricing?: boolean
  buttonText: string
  /**
   * Makes the CTA a link to this page instead of a button, with the ↗ the
   * site's call-to-action buttons carry. The public pricing page uses it to
   * send Enterprise readers to /enterprise.
   */
  buttonHref?: string
  /** Label while `buttonLoading` (defaults to 'Loading…', translated). */
  buttonLoadingText?: string
  buttonVariant?: PricingButtonVariant
  currentButtonHoverText?: string
  onSelect: () => void
  disabled?: boolean
  /**
   * Which surface this card is rendering on. `onboarding` tightens the two
   * internal breathing blocks (above the feature list and above the CTA) and
   * squares the CTA off to match the wizard's button scale — the /setup column
   * is 600px wide, the cards carry three bullets instead of five, and nothing
   * else in that wizard is a pill. The card's own box — radius, padding,
   * background — is identical on both surfaces, so /plan and the public
   * /pricing page are untouched.
   */
  surface?: 'plan' | 'onboarding'
  buttonLoading?: boolean
}

function CurrentButton({
  text,
  hoverText,
  onSelect,
  disabled,
  loading,
}: {
  text: string
  hoverText?: string
  onSelect: () => void
  disabled: boolean
  loading: boolean
}) {
  const t = useTranslations('ui')
  const [isHovered, setIsHovered] = useState(false)
  const isInteractive = !disabled
  const label = isHovered && hoverText ? hoverText : text
  let content: string | ReactNode = label

  if (loading) {
    content = t('pricingCard.opening')
  } else if (isInteractive && hoverText) {
    content = (
      <TextMorph
        as="span"
        className="flex items-center"
        duration={220}
        respectReducedMotion
      >
        {label}
      </TextMorph>
    )
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || loading}
      aria-busy={loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      className={cn(
        pricingCtaClass({
          tone: 'current',
          fullWidth: false,
        }),
        'gap-1.5',
        {
          'cursor-default': !isInteractive,
        }
      )}
    >
      <Check className="size-3.5" aria-hidden="true" />
      {content}
    </button>
  )
}

/**
 * ONE plan card for every pricing surface — the public /pricing page, the
 * in-app /plan grid, and the onboarding promo step all render this shell:
 * a white panel with a hairline outline and a soft shadow, holding the name, a tabular price with its suffix,
 * optional detail/caption lines, check bullets in the flexible middle, and a
 * compact pill CTA. The one solid featured pill is the only strong emphasis.
 */
export function PricingCard({
  name,
  price,
  priceSuffix,
  compareAtPrice,
  priceDetail,
  caption,
  badge,
  secondaryCta,
  features,
  isPopular = false,
  isCurrentPlan = false,
  isCustomPricing = false,
  buttonText,
  buttonHref,
  buttonLoadingText,
  buttonVariant = 'default',
  currentButtonHoverText,
  onSelect,
  disabled = false,
  surface = 'plan',
  buttonLoading = false,
}: PricingCardProps) {
  const t = useTranslations('ui')
  const tCommon = useTranslations('common')
  const isFeaturedCta =
    !isCurrentPlan && (buttonVariant === 'upgrade' || isPopular)
  const primaryLabel = buttonLoading
    ? (buttonLoadingText ?? tCommon('states.loading'))
    : buttonText
  const secondaryLabel = secondaryCta?.loading
    ? t('pricingCard.starting')
    : (secondaryCta?.label ?? '')

  const primaryCtaClass = cn(
    pricingCtaClass({
      tone: isFeaturedCta ? 'featured' : 'outline',
      fullWidth: false,
    }),
    // The wizard has no pills. Tailwind-merge lets this win over the
    // shared pricing language's rounded-full.
    { 'rounded-lg': surface === 'onboarding' }
  )
  let primaryCta: ReactNode
  if (isCurrentPlan) {
    primaryCta = (
      <CurrentButton
        text={buttonText}
        hoverText={currentButtonHoverText}
        onSelect={onSelect}
        disabled={disabled}
        loading={buttonLoading}
      />
    )
  } else if (buttonHref) {
    primaryCta = (
      <Link href={buttonHref} className={cn(primaryCtaClass, 'gap-1.5')}>
        {buttonText}
        <ArrowUpRight className="size-4" aria-hidden="true" />
      </Link>
    )
  } else {
    primaryCta = (
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled || buttonLoading}
        aria-busy={buttonLoading}
        className={primaryCtaClass}
      >
        {primaryLabel}
      </button>
    )
  }

  return (
    <div
      data-plan-card={name}
      className="flex h-full w-full min-w-0 flex-col rounded-lg border border-black/[0.06] bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_12px_32px_-16px_rgba(30,30,30,0.16)] dark:border-white/10 dark:bg-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-medium text-foreground text-xl tracking-tight">
          {name}
        </h3>
        {badge ? (
          <span className="text-caption text-muted-foreground">
            {badge.label}
          </span>
        ) : null}
      </div>

      <div className="mt-1">
        {isCustomPricing ? (
          <span className="font-medium text-2xl text-foreground tracking-tight">
            {t('pricingCard.custom')}
          </span>
        ) : (
          <div className="flex flex-wrap items-baseline gap-1.5">
            {compareAtPrice ? (
              <span className="text-muted-foreground text-sm tabular-nums line-through">
                {compareAtPrice}
              </span>
            ) : null}
            <span className="font-medium text-2xl text-foreground tabular-nums tracking-tight">
              {price}
            </span>
            <span className="text-muted-foreground text-sm">
              {priceSuffix ?? t('pricingCard.perMonth')}
            </span>
          </div>
        )}
        {priceDetail}
      </div>

      {caption ? (
        <p className="mt-3 text-muted-foreground text-xs leading-relaxed">
          {caption}
        </p>
      ) : null}

      <ul
        className={cn('flex min-h-0 flex-1 flex-col gap-2.5', {
          'pt-10': surface === 'plan',
          'pt-6': surface === 'onboarding',
        })}
      >
        {features.map((feature) => (
          <li key={feature.id} className="flex items-start gap-2">
            <Check
              className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-muted-foreground text-sm leading-snug">
              {feature.name}
            </span>
          </li>
        ))}
      </ul>

      <div
        className={cn('flex flex-col items-start gap-2', {
          'pt-10': surface === 'plan',
          'pt-6': surface === 'onboarding',
        })}
      >
        {primaryCta}
        {secondaryCta ? (
          <button
            type="button"
            onClick={secondaryCta.onSelect}
            disabled={secondaryCta.loading}
            aria-busy={secondaryCta.loading}
            className={pricingCtaClass({
              tone: 'ghost',
              size: 'sm',
              fullWidth: false,
            })}
          >
            {secondaryLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}
